import { Inject, Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { SZZA012AService, Szza012aRequest } from 'sg-api';
import { catchError, map, timeout } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ApiCommonService } from './api-common.service';
import { SharedLibConfig } from '../config/shared-lib-config';
import { SHARED_LIB_CONFIG } from '../config/shared-lib-config.token';

/**
 * 時刻関連API
 */
@Injectable({
  providedIn: 'root'
})
export class TimeApiService {

  /** 二重呼び出し防止フラグ */
  // 業務日付取得の呼び出しは一度のみとする。
  public isCalledFlag = false;

  /** 業務日付 */
  private _businessDate$ = new BehaviorSubject<string>('');

  /**
   * コンストラクタ。
   * @memberof TimeApiService
   */
  constructor(private szza012AService: SZZA012AService,
    private apiCommonService: ApiCommonService,
    @Inject(SHARED_LIB_CONFIG) private sharedLibConfig: SharedLibConfig) { }

  /**
   * 業務日付を取得
   *
   * @return {*}  {string}
   * @memberof TimeApiService
   */
  get bussinessDate(): Observable<string> {
    return this._businessDate$.asObservable();
  }

  /**
   * 業務日付を共有変数へ更新する。
   *
   * @memberof TimeApiService
   */
  public updateBussinessDate(): void {

    this.isCalledFlag = true;

    // リクエスト情報生成
    let request: Szza012aRequest = {
      businessDayKbn: 'PCZZ0001'
    };

    // 業務日付を取得
    this.szza012AService.a10RSZZA012APost(this.apiCommonService.setRequestCommon(<any>request), 'response')
      .pipe(
        map(
          response => {
            // エラーが設定されていないかチェックする
            this.apiCommonService.checkError(response);
            // 受け取った情報を格納
            this._businessDate$.next(response.body.todayDay);
          }),

        timeout(Number(this.sharedLibConfig.httpTimeout)),
        catchError((err) => {
          return throwError(err.message);
        })
      )
      .subscribe();
  }

  /**
   * 現在日付をyyyyMMddで取得
   *
   * @return {*}  {string}
   * @memberof TimeApiService
   */
  public getNowDateString(): string {
    const date = this.convertStringToDate(this._businessDate$.getValue());
    const yyyy = date.getFullYear();
    const MM = ('00' + (date.getMonth() + 1)).slice(-2);
    const dd = ('00' + (date.getDate())).slice(-2);

    return yyyy + MM + dd;
  }

  /**
   * 現在時刻をHHmmで取得
   *
   * @return {*}  {string}
   * @memberof TimeApiService
   */
  public getNowTimeString(): string {
    const date = new Date();
    const HH = ('00' + (date.getHours())).slice(-2);
    const mm = ('00' + (date.getMinutes())).slice(-2);

    return HH + mm;
  }

  /**
   * yyyyMMdd形式の文字列をDate型に変換する
   *
   * @param {string} date
   * @return {*}  {Date}
   * @memberof TimeApiService
   */
  public convertStringToDate(date: string): Date | null {
    let result: Date | null = null;
    if (date && date != '') {
      const yyyy = Number(date.slice(0, 4));
      const MM = Number(date.slice(4, 6)) - 1;
      const dd = Number(date.slice(6, 8));
      result = new Date(yyyy, MM, dd);
    }
    return result;
  }

  /**
   * HHmmss or HHmm形式の文字列をHH:mm:ssに変換する
   *
   * @param {string} date
   * @return {*}  {string}
   * @memberof TimeApiService
   */
  public convertStringToTime(time: string): string {
    let result = '';
    if (time && time != '') {
      const hh = time.slice(0, 2);
      const mm = time.slice(2, 4);
      let ss = time.slice(4, 6);
      if (ss == '') {
        ss = '00';
      }
      result = hh + ':' + mm + ':' + ss;
    }
    return result;
  }

  /**
   * 対象が日付型か判定する
   *
   * @param {*} target
   * @return {*}  {boolean}
   * @memberof TimeApiService
   */
  public isDate(target: any): boolean {
    return Boolean(target && Object.prototype.toString.call(target) === '[object Date]');
  }

  /**
   * Date型の日付をyyyyMMdd形式の文字列に変換する
   *
   * @param {Date} date
   * @return {*}  {string}
   * @memberof TimeApiService
   */
  public convertDateToString(date: Date): string {
    let result = '';
    if (date && Object.prototype.toString.call(date) === '[object Date]') {
      const yyyy = date.getFullYear();
      const MM = ('00' + (date.getMonth() + 1)).slice(-2);
      const dd = ('00' + date.getDate()).slice(-2);
      result = yyyy + MM + dd;
    }
    return result;
  }

  /**
   * yyyyMM形式の文字列をDate型に変換する
   * (日は月の開始日(1日)として変換)
   *
   * @param {string} date
   * @return {*}  {Date}
   * @memberof TimeApiService
   */
  public convertYyyyMmToDate(date: string): Date | null {
    let result: Date | null = null;
    if (date && date != '') {
      const yyyy = Number(date.slice(0, 4));
      const MM = Number(date.slice(4, 6)) - 1;
      result = new Date(yyyy, MM, 1);
    }
    return result;
  }

  /**
   * Date型の日付をyyyyMM形式の文字列に変換する
   *
   * @param {Date} date
   * @return {*}  {string}
   * @memberof TimeApiService
   */
  public convertDateToStringYyyyMm(date: Date): string {
    let result = '';
    if (date && Object.prototype.toString.call(date) === '[object Date]') {
      const yyyy = date.getFullYear();
      const MM = ('00' + (date.getMonth() + 1)).slice(-2);
      result = yyyy + MM;
    }
    return result;
  }

  /**　
   * 日数取得 from-to 間の日数を取得する
   * @param dayFrom 日付from
   * @param dayTo 日付to
   * @param removalEndDate 終了日を日数に含めない場合に設定
   * @returns from-to 間の日数
   **/
  public getDays(dayFrom: Date, dayTo: Date, removalEndDate?: boolean): number {
    const diff = dayTo.getTime() - dayFrom.getTime();
    return removalEndDate ? (diff / 86400000) : (diff / 86400000) + 1;
  }

  /** 　
   * 年月日比較
   * from が小さければtrue　(同日はfalse)
   * 同日をOKにしたい場合はbooleanを設定
   * @param from 比較対象日付 from
   * @param to 比較対象日付 to
   * @param sameDayTrue 同日をtrueにする場合
   * @returns 比較結果
   */
  public compDateYyyyMmDd(from: Date, to: Date, sameDayTrue?: boolean): boolean {
    if (to == null) {
      return true;
    }

    const yearFrom = from.getFullYear();
    const monthFrom = from.getMonth() + 1;
    const dayFrom = from.getDate();
    const yearTo = to.getFullYear();
    const monthTo = to.getMonth() + 1;
    const dayTo = to.getDate();

    if (yearFrom == yearTo) {
      if (monthFrom == monthTo) {
        if (sameDayTrue) {
          return dayFrom <= dayTo;
        } else {
          return dayFrom < dayTo;
        }
      }
      else {
        return monthFrom < monthTo;
      }
    } else {
      return yearFrom < yearTo;
    }
  }

  /**
   * 日付同日判定
   *
   * @param {Date} date1 比較日付１
   * @param {Date} date2 比較日付2
   * @return {boolean} 判定結果
   * @memberof OnlineChargeContractInfoComponent
   */
  public equalDateYyyyMmDd(date1: Date, date2: Date): boolean {

    const year1 = date1.getFullYear();
    const month1 = date1.getMonth() + 1;
    const day1 = date1.getDate();
    const year2 = date2.getFullYear();
    const month2 = date2.getMonth() + 1;
    const day2 = date2.getDate();

    if (year1 == year2) {
      if (month1 == month2) {
        return day1 == day2;
      }
      else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * 日数文字列（mm/dd）取得
   * 
   * @private
   * @param target 文字列取得対象日付
   * @returns 変換した文字列
   */
  public static getDayStrMmDd(target: Date): string {
    const localMonth: number = target.getMonth() + 1;
    const month = (localMonth < 10) ? '0' + localMonth : localMonth;
    const date = (target.getDate() < 10) ? '0' + target.getDate() : target.getDate();
    return month + "/" + date;
  }

  /**
   * DatePickerの開始日付を設定する
   * @param stDate 開始日付
   * @param control 日付FormControl
   */
  public setStartDate(stDate: Date, control: UntypedFormControl): void {
    (control.value != null && control.value != '') ?
      stDate.setTime(control.value.getTime()) :
      stDate.setTime(this.convertStringToDate(this._businessDate$.getValue()).getTime());

    control.valueChanges.subscribe(value => {
      (value == null || value == '') ?
        stDate.setTime(this.convertStringToDate(this.getNowDateString()).getTime()) :
        stDate.setTime(control.value.getTime());
    });
  }
}
